import { v4 as uuidv4 } from 'uuid';
import { computed } from 'vue';
import { useStore } from 'vuex';

import { SetCurrentConversationName } from '~/constants/actions';
import { CHUNK_TYPE, RESPONSE_TYPE } from '~/constants/conversationModels';
import { GetCurrentConversation } from '~/constants/getters';
import {
  AddChunk,
  AddChunkSSE,
  AddMessage,
  AddToastNotification,
  RemoveMessages,
  SetIsMessageLoading,
  SetQueryValue,
  UpdateImageGenImage,
  UpdateMessageId,
} from '~/constants/mutations';

import { gotoConversationSSE, resumeConversationSSE } from '~/services/api/conversations';
import { buildAssistQuery } from '~/utils/new-agent-utils';
import useImageGeneration from './useImageGeneration';

/**
 * New-agent-only SSE mechanics: consuming the assist stream, guardrails rollback, and the
 * resume/goto entry points (resumeConversation, changeImageStyle). Split out of
 * useConversations.js since none of it applies to Authoring — Authoring stays on the old
 * (Ably) agent indefinitely, so keeping this separate means evolving or eventually deleting
 * new-agent behavior never touches Authoring's code path.
 */
export default () => {
  const store = useStore();
  const { getUserImages } = useImageGeneration();

  const currentConversation = computed(() => store.getters[`conversationStore/${GetCurrentConversation}`]);

  /**
   * Sub-event carried on the `custom:image_gen` SSE channel. One image-started
   * per requested image, then image-finished for each; anything else is treated
   * as a failure for that image by the store.
   * @typedef {{ event: 'image-started'|'image-finished'|string, imageId: string, params?: object }} ImageGenEvent
   */

  /**
   * Fetches and commits the resolved url for one or more finished image-gen images. Shared
   * between the live stream handler below (one id at a time, as each image-finished event
   * arrives) and ImageGenBlockSSE.vue's mount-time resolution (a whole reloaded chunk's
   * already-finished-but-urlless images at once) — same fetch, same mutation, one place
   * instead of two copies that could drift apart.
   * @param {string[]} imageIds
   * @param {{messageId: string, currentConversationId: string}} target
   */
  const resolveImageGenUrls = async (imageIds, { messageId, currentConversationId }) => {
    if (!imageIds.length) return;

    const userConversationId = currentConversation.value?.userId ?? '';
    const images = await getUserImages({ ids: imageIds, userConversationId });

    images.forEach((image) => {
      store.commit(`conversationStore/${UpdateImageGenImage}`, {
        messageId,
        currentConversationId,
        imageId: image.id,
        image,
      });
    });
  };

  // TODO
  // FastAPI returns 422 for any request-body validation failure, not just guardrails
  // rejections — a malformed body from an FE bug gets the same status. There's no structured
  // error code yet to tell the two apart, so we match on the guardrails copy itself. Fragile
  // against backend wording changes (ask backend for a structured error code if this becomes
  // a maintenance problem); failing to match just falls through to the generic error bubble
  // instead of silently misreporting an FE bug as a content rejection.
  const GUARDRAILS_DETAIL_MARKER = 'triggered our safety guidelines';

  /**
   * Guardrails rejections (422, matched via GUARDRAILS_DETAIL_MARKER) respond before the
   * stream opens, so nothing was persisted server-side. Roll back the failed turn instead of
   * leaving an error bubble in the chat history — applies to any streamConversationResponse
   * caller (publishAssistMessage's /stream and remix /commands/goto, resumeConversation,
   * changeImageStyle's /commands/goto).
   *
   * Only publishAssistMessage passes a userMessageId (a real user-typed message was
   * added alongside the placeholder); when present, that message is also removed and
   * its text handed back to the input for editing. resumeConversation/changeImageStyle
   * only ever add the response placeholder, so there's nothing to restore — just clean
   * up the placeholder and surface the toast.
   *
   * @returns {boolean} True if this was a guardrails rejection and it was fully handled;
   *   false for any other 422 (falls through to the generic error bubble).
   */
  const handleGuardrailsRejection = ({ error, userMessageId, responseMessageId }) => {
    if (!error.message?.includes(GUARDRAILS_DETAIL_MARKER)) {
      return false;
    }

    // Read the rejected text before removing the message it lives on.
    const rejectedQuery = userMessageId
      ? currentConversation.value.messages?.find((msg) => msg.messageId === userMessageId)?.message ?? ''
      : null;

    const messageIdsToRemove = userMessageId ? [userMessageId, responseMessageId] : [responseMessageId];
    store.dispatch(`conversationStore/${RemoveMessages}`, messageIdsToRemove);

    if (userMessageId) {
      store.dispatch(`conversationStore/${SetQueryValue}`, rejectedQuery);
    }

    store.commit(`conversationStore/${SetIsMessageLoading}`, false);
    store.commit(`notificationStore/${AddToastNotification}`, {
      type: 'toast',
      title: error.message,
      notification: { externalId: uuidv4() },
      createdAt: Date.now(),
    });
    return true;
  };

  /**
   * Recovery strategies for stream errors that need special handling beyond the generic
   * error bubble, keyed by HTTP status. Add an entry here (and a handler above) for each
   * new status the backend introduces instead of growing the catch block below.
   * Each handler returns true if it fully handled the error, or false to fall through
   * to the generic error bubble.
   */
  const STREAM_ERROR_HANDLERS = {
    422: handleGuardrailsRejection,
  };

  /**
   * Consume an assist SSE stream and dispatch each event into the conversation store.
   *
   * @param {import('~/services/api/conversations').SSEEventStream} stream
   * @param {string} responseMessageId Message the chunks are appended to.
   * @param {string} currentConversationId Guards against stale streams after a conversation switch.
   * @param {{ userMessageId?: string }} [rollback] Passed through to STREAM_ERROR_HANDLERS —
   *   e.g. handleGuardrailsRejection needs userMessageId to roll back the right messages.
   */
  const streamConversationResponse = async (
    stream,
    responseMessageId,
    currentConversationId,
    { userMessageId } = {},
  ) => {
    console.log('stream', stream);
    // Helper to process events
    const processEvent = ({ event, data }) => {
      console.log('event', event, data);
      switch (event) {
        case 'messages': {
          const chunk = Array.isArray(data) ? data[0] : data;
          if (!chunk) break;
          if (chunk.event === 'content-block-delta' && chunk.delta?.type === 'text-delta') {
            store.dispatch(`conversationStore/${AddChunk}`, {
              responseType: RESPONSE_TYPE.messages,
              messageId: responseMessageId,
              currentConversationId,
              message: { chunk: chunk.delta.text, chunk_type: CHUNK_TYPE.TEXT },
            });
          }
          break;
        }
        case 'interrupts': {
          if (Array.isArray(data) && data.length > 0) {
            store.dispatch(`conversationStore/${AddChunkSSE}`, {
              chunkType: CHUNK_TYPE.INTERRUPT,
              messageId: responseMessageId,
              currentConversationId,
              data,
            });
          }
          break;
        }
        case 'tools': {
          console.log('Tool event with subevent:', data);
          store.dispatch(`conversationStore/${AddChunkSSE}`, {
            chunkType: CHUNK_TYPE.TOOL_CALLS,
            messageId: responseMessageId,
            currentConversationId,
            data: data,
          });
          break;
        }
        case 'custom:image_gen': {
          store.dispatch(`conversationStore/${AddChunkSSE}`, {
            chunkType: CHUNK_TYPE.IMAGE_GEN,
            messageId: responseMessageId,
            currentConversationId,
            data,
          });
          if (data.event === 'image-finished') {
            resolveImageGenUrls([data.imageId], { messageId: responseMessageId, currentConversationId });
          }
          break;
        }
        case 'execution_error': {
          // `data` carries `source`, which decides blocking vs non-blocking in applyEventToChunks —
          // forwarding it here keeps live and history branching on exactly the same signal.
          store.dispatch(`conversationStore/${AddChunkSSE}`, {
            chunkType: CHUNK_TYPE.ERROR,
            messageId: responseMessageId,
            currentConversationId,
            data,
          });
          break;
        }
        case 'references': {
          console.log('References event with subevent:', data);
          store.dispatch(`conversationStore/${AddChunkSSE}`, {
            chunkType: CHUNK_TYPE.REFERENCES,
            messageId: responseMessageId,
            currentConversationId,
            data: data,
          });
          break;
        }
        case 'conversation': {
          // Auto-generated title, emitted once per conversation (replaces old method over Ably `naming-response` channel)
          store.dispatch(`conversationStore/${SetCurrentConversationName}`, {
            conversationId: data.conversationId,
            new_title: data.title,
          });
          break;
        }
        case 'turn': {
          // Dedicated event carrying the real turn id - `status` goes 'pending' (id assigned,
          // not yet committed) then 'stored' (persisted in Postgres). turnId is stable across
          // both, so this runs for every 'turn' event rather than only the first
          if (data.turnId) {
            store.commit(`conversationStore/${UpdateMessageId}`, {
              oldMessageId: responseMessageId,
              newMessageId: data.turnId,
              status: data.status,
              currentConversationId,
            });
            responseMessageId = data.turnId;
          }
          break;
        }
        default: {
          console.log('Other event', event, data);
        }
      }
    };

    try {
      for await (const { event, data } of stream) {
        processEvent({ event, data });
        await new Promise((resolve) => requestAnimationFrame(resolve));
      }

      store.dispatch(`conversationStore/${AddChunk}`, {
        responseType: RESPONSE_TYPE.endMessage,
        messageId: responseMessageId,
        currentConversationId,
      });
    } catch (error) {
      const handled = STREAM_ERROR_HANDLERS[error.status]?.({ error, userMessageId, responseMessageId });
      if (handled) return;

      console.error('Stream error:', error);
      store.dispatch(`conversationStore/${AddChunk}`, {
        responseType: RESPONSE_TYPE.error,
        messageId: responseMessageId,
        currentConversationId,
        message: error.message || 'Stream connection failed',
      });
    }
  };

  /**
   * Opens a fresh response message to stream a new graph turn into, outside the
   * normal user-query flow (resume continuations, direct goto jumps).
   */
  const startNewResponseMessage = () => {
    const responseMessageId = uuidv4();
    const conversationId = currentConversation.value?.conversationId;
    store.dispatch(`conversationStore/${AddMessage}`, {
      data: null,
      currentCollection: {},
      responseMessageId,
      filters: [],
    });
    return { responseMessageId, conversationId };
  };

  /**
   * Resume a paused graph after an `interrupts` event and stream the
   * continuation into a new response message.
   *
   * @param {import('~/services/api/conversations').ResumeCommandPayload} payload
   *   Discriminated on `type`: 'imageStyleChoicesResponse' or 'hitlResponse'.
   */
  const resumeConversation = async (payload) => {
    const { responseMessageId, conversationId } = startNewResponseMessage();
    const stream = resumeConversationSSE(conversationId, payload);
    await streamConversationResponse(stream, responseMessageId, conversationId);
  };

  /**
   * Re-enters the image_generation node directly (POST commands/goto) with a new aspect
   * ratio / style / count, for a run that has already completed. Streams the result into
   * a new response message since there's no pending interrupt to resume.
   *
   * `imageGenerationParams.remixImageId` is passed through as given by the caller so a
   * restyle of a remixed image keeps regenerating from the same source image.
   *
   * @param {string|null} originalQuery The prompt that produced the images being restyled.
   * @param {import('~/services/api/conversations').ImageGenerationParams} imageGenerationParams
   */
  const changeImageStyle = async (originalQuery, imageGenerationParams) => {
    const { responseMessageId, conversationId } = startNewResponseMessage();
    const stream = gotoConversationSSE(conversationId, {
      goto: 'image_generation',
      // Restyling is always plain text, never a prompt card — reuse buildAssistQuery with
      // an empty promptCardsData so it always takes the TextQuery branch.
      query: buildAssistQuery(originalQuery, {}),
      imageGenerationParams,
    });
    await streamConversationResponse(stream, responseMessageId, conversationId);
  };

  return {
    streamConversationResponse,
    resumeConversation,
    changeImageStyle,
    resolveImageGenUrls,
  };
};
